const generation = {
    system: 'OpenAI Codex AI assistant',
    date: '26 August 2026',
    status: 'Human review pending',
    note: 'The AI synthesized the content from the listed biblical, historical, scholarly, and ecclesial sources. Associations describe broad affinities, not formal endorsements by every person or tradition represented.',
};

const source = (id, author, title, detail, href) => ({ id, author, title, detail, href });

export const moralQuestionPerspectives = [
    {
        slug: 'is-it-ever-okay-to-lie',
        category: 'Christian Ethics',
        question: 'Is it ever okay for a Christian to lie?',
        shortTitle: 'Is it ever okay to lie?',
        summary: 'Christians agree that God is truthful, that deceit corrodes trust, and that ordinary lying is sinful. They disagree about extreme cases in which truthful disclosure would assist grave injustice: whether direct falsehood remains forbidden, whether silence or discreet speech is the only faithful option, whether a higher duty can justify deception, or whether deception can be the least-wrong action in a tragic conflict.',
        introduction: 'The disagreement is not between people who value truth and people who do not. It concerns what truthfulness requires when duties appear to collide, whether an unjust aggressor has any claim to the truth, how biblical accounts of the Hebrew midwives and Rahab should function in moral reasoning, and whether a faithful decision in a fallen situation can still involve guilt or moral loss.',
        seoDescription: 'Compare Christian views on whether lying is ever permissible, including strict truthfulness, discreet silence, higher duties, and tragic conflicts.',
        lastModified: '2026-08-26',
        keyTexts: [
            { label: 'Exodus 1:15–21', href: '/read/Exod.1/#Exod.1.15-Exod.1.21' },
            { label: 'Joshua 2:1–14', href: '/read/Josh.2/#Josh.2.1-Josh.2.14' },
            { label: 'Proverbs 12:17–22', href: '/read/Prov.12/#Prov.12.17-Prov.12.22' },
            { label: 'Ephesians 4:20–32', href: '/read/Eph.4/#Eph.4.20-Eph.4.32' },
        ],
        readings: [
            {
                id: 'direct-falsehood-never-permitted',
                title: 'Christians should not lie',
                thesis: 'A Christian may remain silent or refuse an unjust demand, but may not intentionally assert what is false, even to secure a good result.',
                accent: 'navy',
                badges: ['Augustinian', 'Thomist', 'Catholic', 'Conservative'],
                summary: 'This reading treats truthful speech as a moral good whose basic integrity cannot be suspended by circumstances. Augustine and Aquinas distinguish lies by intention and seriousness, but maintain that a good purpose does not turn a false assertion into a good act. Biblical figures may be praised for faith, courage, or protection of life without every means they used being morally approved. Faced with an aggressor, the believer may refuse to answer, resist, redirect, or prudently conceal information, but should not deliberately speak a falsehood.',
                contribution: 'It protects truthfulness from becoming merely instrumental and takes seriously the damage deception does to speech, trust, and the speaker’s own character. It also avoids making moral permission depend on uncertain predictions about consequences.',
                caution: 'The view must explain how its alternatives work when silence itself reveals the victim’s location or when an aggressor controls every available response. Critics also ask whether its account adequately reflects biblical commendation of people whose deception protected life.',
                supporters: [
                    { name: 'Augustine of Hippo', sourceId: 'augustine-on-lying' },
                    { name: 'Thomas Aquinas', sourceId: 'aquinas-lying' },
                ],
                predicates: [
                    {
                        label: 'Holiness conforms speech and character to the truthful life of Christ',
                        topicSlug: 'what-is-holiness',
                        readingId: 'christlike-spirit-formed-life',
                        linked: true,
                    },
                    {
                        label: 'God’s moral instruction witnesses to the shape of faithful obedience',
                        topicSlug: 'what-can-the-law-do-if-it-cannot-justify',
                        readingId: 'guides-covenant-life',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'higher-duty-protects-innocent', linked: true },
                    { type: 'overlaps with', target: 'discretion-without-falsehood', linked: true },
                    {
                        type: 'depends on',
                        label: 'What is holiness?',
                        topicSlug: 'what-is-holiness',
                        readingId: 'christlike-spirit-formed-life',
                        linked: true,
                    },
                ],
            },
            {
                id: 'discretion-without-falsehood',
                title: 'Discretion can avoid lying',
                thesis: 'Truthfulness does not require disclosing every fact to every questioner, so silence, refusal, secrecy, or carefully limited speech may protect others without making a false assertion.',
                accent: 'teal',
                badges: ['Catholic', 'Reformed', 'Pastoral', 'Moderate'],
                summary: 'This approach distinguishes lying from the broader act of withholding information. A person has no unlimited right to another person’s secrets, especially when disclosure would enable abuse, betrayal, or grave harm. The Catechism of the Catholic Church therefore joins condemnation of lying to a duty of discretion, while the Westminster Larger Catechism treats both harmful falsehood and speaking truth at the wrong time or for a malicious purpose as failures. The practical aim is neither full disclosure nor fabricated speech, but an answer governed by truth, justice, charity, privacy, and safety.',
                contribution: 'It rejects the assumption that the only choices are betraying the innocent or telling a direct lie. It also supplies a useful ethic for confidentiality, pastoral care, medicine, safeguarding, journalism, and private information.',
                caution: 'In a coercive encounter, discreet wording may function as deception even without a formally false sentence, and silence may be impossible. The view must guard against verbal technicalities that preserve literal accuracy while intentionally creating a harmful false impression.',
                supporters: [
                    { name: 'Catechism of the Catholic Church', sourceId: 'vatican-respect-truth' },
                    { name: 'Westminster Assembly', sourceId: 'westminster-ninth-commandment' },
                ],
                predicates: [
                    {
                        label: 'Personal holiness includes social love, justice, and protection of neighbours',
                        topicSlug: 'how-should-holiness-be-recognised',
                        readingId: 'personal-and-social-love',
                        linked: true,
                    },
                    {
                        label: 'A command’s purpose must be applied faithfully within its concrete context',
                        topicSlug: 'how-should-christians-apply-culturally-specific-apostolic-commands',
                        readingId: 'preserve-purpose-adapt-form',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'direct-falsehood-never-permitted', linked: true },
                    { type: 'contrasts with', target: 'higher-duty-protects-innocent', linked: true },
                    { type: 'overlaps with', target: 'tragic-conflict-responsible-action', linked: true },
                ],
            },
            {
                id: 'higher-duty-protects-innocent',
                title: 'Protecting life may justify deception',
                thesis: 'When genuine moral duties conflict, protecting innocent life or resisting grave injustice can take priority over the ordinary duty to tell the truth.',
                accent: 'amber',
                badges: ['Evangelical', 'Graded Absolutism', 'Conservative'],
                summary: 'This view retains objective moral commands but understands them as ordered rather than flat. In an unavoidable conflict, a person obeys the greater duty and is not guilty for setting aside the lesser one. Norman Geisler presents this as graded absolutism. Applied to lying, the Hebrew midwives and Rahab are read not merely as faithful despite their deception but as examples of allegiance to God and protection of life taking precedence over an oppressor’s demand for truthful cooperation.',
                contribution: 'It gives a direct answer to cases in which silence, refusal, or evasion cannot protect a victim. It also recognises that telling the truth to assist murder, abuse, or persecution can become participation in the aggressor’s wrongdoing.',
                caution: 'People readily exaggerate conflicts and label a preferred outcome the “higher duty”. This approach therefore needs strict tests: the danger must be grave, the conflict genuine, the deception narrowly directed against injustice, and ordinary truthful alternatives unavailable.',
                supporters: [
                    { name: 'Norman L. Geisler', sourceId: 'geisler-absolutes' },
                ],
                predicates: [
                    {
                        label: 'Love of neighbour includes active protection from grave harm',
                        topicSlug: 'how-should-holiness-be-recognised',
                        readingId: 'personal-and-social-love',
                        linked: true,
                    },
                    {
                        label: 'Biblical commands guide covenant life and must be read together',
                        topicSlug: 'what-can-the-law-do-if-it-cannot-justify',
                        readingId: 'guides-covenant-life',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'direct-falsehood-never-permitted', linked: true },
                    { type: 'overlaps with', target: 'tragic-conflict-responsible-action', linked: true },
                    {
                        type: 'raises',
                        label: 'How should holiness be recognised?',
                        topicSlug: 'how-should-holiness-be-recognised',
                        linked: true,
                    },
                ],
            },
            {
                id: 'tragic-conflict-responsible-action',
                title: 'Choose the least harmful course',
                thesis: 'Extreme situations may make protective deception the responsible action without turning it into a clean moral good or removing the need for humility, accountability, and grace.',
                accent: 'red',
                badges: ['Lutheran', 'Christian Realist', 'Moderate'],
                summary: 'This reading resists both a simple exception and a purely rule-based answer. Bonhoeffer’s ethics emphasises truthful speech as concrete responsibility before God and neighbour rather than detached recital of facts. A related tragic-conflict account says that a fallen situation can leave no morally untouched option: deceiving an unjust aggressor may be the least sinful and most responsible act, yet the agent should not celebrate deception or pretend that violence done to truth has disappeared. Responsibility includes accepting moral cost and seeking forgiveness and repair where possible.',
                contribution: 'It takes coercion, power, relationship, and historical danger seriously. It also guards against moral self-congratulation by acknowledging that courageous action in a damaged world can involve guilt, grief, and dependence on grace.',
                caution: 'If every difficult context is called tragic, stable moral norms can dissolve into personal judgement. The approach must distinguish extraordinary coercion from ordinary embarrassment, convenience, conflict avoidance, or self-protection.',
                supporters: [
                    { name: 'Dietrich Bonhoeffer', sourceId: 'bonhoeffer-ethics' },
                    { name: 'Stewart Clem', sourceId: 'clem-nazi-door' },
                ],
                predicates: [
                    {
                        label: 'Christian growth remains dependent on grace amid incomplete moral formation',
                        topicSlug: 'what-is-sanctification',
                        readingId: 'progressive-holiness',
                        linked: true,
                    },
                    {
                        label: 'Christlike holiness joins truth, love, and concrete responsibility',
                        topicSlug: 'what-is-holiness',
                        readingId: 'christlike-spirit-formed-life',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'higher-duty-protects-innocent', linked: true },
                    { type: 'contrasts with', target: 'direct-falsehood-never-permitted', linked: true },
                    { type: 'overlaps with', target: 'discretion-without-falsehood', linked: true },
                ],
            },
        ],
        sources: [
            source(
                'augustine-on-lying',
                'Augustine of Hippo',
                'On Lying',
                'Augustine’s primary treatment of lying and his argument that a good intention does not make deliberate falsehood morally permissible.',
                'https://www.newadvent.org/fathers/1312.htm',
            ),
            source(
                'aquinas-lying',
                'Thomas Aquinas',
                'Summa Theologiae II-II, Question 110: The Vices Opposed to Truth, and First of Lying',
                'Aquinas distinguishes kinds and gravity of lies while arguing that every direct lie is opposed to truth; he also permits prudent concealment of truth.',
                'https://www.newadvent.org/summa/3110.htm',
            ),
            source(
                'vatican-offences-truth',
                'Catechism of the Catholic Church',
                'Offences Against Truth',
                'The official Catholic account of lying, intention, culpability, harm, justice, and the social damage caused by deception.',
                'https://www.vatican.va/content/catechism/en/part_three/section_two/chapter_two/article_8/iii_offenses_against_truth.html',
            ),
            source(
                'vatican-respect-truth',
                'Catechism of the Catholic Church',
                'Respect for the Truth',
                'The official Catholic account of discretion, privacy, confidentiality, safety, and the limits of another person’s right to information.',
                'https://www.vatican.va/content/catechism/en/part_three/section_two/chapter_two/article_8/iv_respect_for_the_truth.html',
            ),
            source(
                'westminster-ninth-commandment',
                'Westminster Assembly',
                'Westminster Larger Catechism, Questions 143–145',
                'A Reformed treatment of the ninth commandment that condemns lying while also addressing secrecy, harmful disclosure, untimely truth, and duties to a neighbour’s good name.',
                'https://opc.org/lc.html',
            ),
            source(
                'geisler-absolutes',
                'Norman L. Geisler',
                'Any Absolutes? Absolutely!',
                'Geisler’s defence of graded absolutism: when genuine moral absolutes conflict, the Christian is responsible to obey the greater commandment.',
                'https://www.equip.org/articles/any-absolutes-absolutely/',
            ),
            source(
                'bonhoeffer-ethics',
                'Dietrich Bonhoeffer',
                'Ethics',
                'The official publisher page describes Bonhoeffer’s concrete, Christ-centred account of responsible Christian action in real institutions and situations.',
                'https://www.simonandschuster.com/books/Ethics/Dietrich-Bonhoeffer/9781451688504',
            ),
            source(
                'clem-nazi-door',
                'Stewart Clem',
                'Lying to the Nazi at the Door: A Thomistic Reframing of the Classic Moral Dilemma',
                'A peer-reviewed theological argument that protective lying may be the least sinful action in a tragic conflict without becoming morally good in itself.',
                'https://onlinelibrary.wiley.com/doi/10.1111/jore.12338',
            ),
        ],
        generation,
    },
];
